/**
 * Shared rule-type metadata. Drives the rule-type picker and the icon/label
 * lookup used by the inline rule editor and the legacy builder alike.
 *
 * Adding a new rule type means: (1) extend `DataPlatformAudienceRule.type` in
 * data-platform.model.ts, (2) add an entry here, (3) extend the editor's
 * per-type input block, and (4) extend dpRuleToAudienceFilter +
 * mapAudienceToRules if the rule needs to round-trip through Audience.filters.
 */

export interface RuleTypeConfig {
  type: string;
  /** Material icon name. */
  icon: string;
  /** Translation key for the label shown in the picker / on rule cards. */
  labelKey: string;
  /** Translation key for the short description shown in the picker. */
  descKey: string;
  /**
   * Account tiers on which this rule is locked. Picker shows a `PRO` badge
   * for disabled entries and clicking them opens the upgrade popup instead
   * of adding the rule. Mirrors the legacy `AudienceFilterConfig.disabledOnTiers`
   * field — keep in sync with the corresponding entry in
   * `audience-helpers.ts` `getAllFilterConfigGroups`.
   */
  disabledOnTiers?: ReadonlyArray<string>;
}

export interface RuleTypeGroup {
  /** Translation key for the group heading shown in the picker. */
  labelKey: string;
  configs: RuleTypeConfig[];
}

// Tier values are duplicated here as string literals to avoid pulling
// `Tier` (and its dependency chain) into a static config file. Keep in sync
// with `Tier` in `src/app/shared/account/tier.model.ts`.
const ESSENTIAL_TIER: readonly string[] = ['Essential'];

export const RULE_TYPE_GROUPS: readonly RuleTypeGroup[] = [
  {
    labelKey: 'data-platform.group-identity',
    configs: [
      {
        type: 'is_identified',
        icon: 'badge',
        labelKey: 'data-platform.rule-is-identified',
        descKey: 'data-platform.rule-desc-is-identified',
      },
      {
        type: 'is_anonymous',
        icon: 'person_off',
        labelKey: 'data-platform.rule-is-anonymous',
        descKey: 'data-platform.rule-desc-is-anonymous',
      },
      // Consent maps to AudienceFilterType.Consent in the legacy config, which
      // is gated on Essential. Mirror that for the DP-rule entries.
      {
        type: 'has_consent',
        icon: 'verified_user',
        labelKey: 'data-platform.rule-has-consent',
        descKey: 'data-platform.rule-desc-has-consent',
        disabledOnTiers: ESSENTIAL_TIER,
      },
      {
        type: 'has_not_consent',
        icon: 'gpp_bad',
        labelKey: 'data-platform.rule-has-not-consent',
        descKey: 'data-platform.rule-desc-has-not-consent',
        disabledOnTiers: ESSENTIAL_TIER,
      },
    ],
  },
  {
    // Login state is read live from the visit (the customer's site auth), not the
    // stored profile, so it's its own group and the picker sides it as "visit".
    labelKey: 'data-platform.group-login',
    configs: [
      {
        type: 'is_logged_in',
        icon: 'login',
        labelKey: 'data-platform.rule-is-logged-in',
        descKey: 'data-platform.rule-desc-is-logged-in',
      },
      {
        type: 'is_logged_out',
        icon: 'logout',
        labelKey: 'data-platform.rule-is-logged-out',
        descKey: 'data-platform.rule-desc-is-logged-out',
      },
    ],
  },
  {
    // Engagement score + the actions behind it; folded together so the score
    // isn't a lone one-item group.
    labelKey: 'data-platform.group-activity',
    configs: [
      {
        type: 'engagement_score',
        icon: 'trending_up',
        labelKey: 'data-platform.rule-engagement-score',
        descKey: 'data-platform.rule-desc-engagement-score',
      },
      {
        type: 'event_count',
        icon: 'touch_app',
        labelKey: 'data-platform.rule-event-count',
        descKey: 'data-platform.rule-desc-event-count',
      },
      // Kept for back-compat but hidden from the picker (HIDDEN_PICKER_RULE_TYPES);
      // widget interaction is added as a Campaign* condition and promoted to this
      // rule for reach (see reach-estimate-factors).
      {
        type: 'widget_event_count',
        icon: 'widgets',
        labelKey: 'data-platform.rule-widget-event-count',
        descKey: 'data-platform.rule-desc-widget-event-count',
      },
      // Goals filter is gated on Essential.
      {
        type: 'goal_count',
        icon: 'flag',
        labelKey: 'data-platform.rule-goal-count',
        descKey: 'data-platform.rule-desc-goal-count',
        disabledOnTiers: ESSENTIAL_TIER,
      },
      {
        type: 'total_events',
        icon: 'bar_chart',
        labelKey: 'data-platform.rule-total-events',
        descKey: 'data-platform.rule-desc-total-events',
      },
    ],
  },
  {
    // "Recency & frequency" = how recently / how often they visit.
    labelKey: 'data-platform.group-recency',
    configs: [
      {
        type: 'session_count',
        icon: 'visibility',
        labelKey: 'data-platform.rule-visit-count',
        descKey: 'data-platform.rule-desc-visit-count',
      },
      {
        type: 'last_active',
        icon: 'schedule',
        labelKey: 'data-platform.rule-last-active',
        descKey: 'data-platform.rule-desc-last-active',
      },
      {
        type: 'first_seen',
        icon: 'person_add',
        labelKey: 'data-platform.rule-first-seen',
        descKey: 'data-platform.rule-desc-first-seen',
      },
    ],
  },
  {
    labelKey: 'data-platform.group-purchases',
    configs: [
      {
        type: 'purchase_count',
        icon: 'shopping_cart',
        labelKey: 'data-platform.rule-purchase-count',
        descKey: 'data-platform.rule-desc-purchase-count',
      },
      {
        type: 'revenue',
        icon: 'attach_money',
        labelKey: 'data-platform.rule-revenue',
        descKey: 'data-platform.rule-desc-revenue',
      },
    ],
  },
  {
    // "Interests & tags" = what they're into (browsing affinities) and the
    // tags/labels they carry.
    labelKey: 'data-platform.group-interests',
    configs: [
      {
        type: 'dimensions',
        icon: 'category',
        labelKey: 'data-platform.dimensions',
        descKey: 'data-platform.rule-desc-action-dimension',
      },
      // Tags filter is gated on Essential.
      {
        type: 'has_tag',
        icon: 'label',
        labelKey: 'data-platform.rule-has-tag',
        descKey: 'data-platform.rule-desc-has-tag',
        disabledOnTiers: ESSENTIAL_TIER,
      },
    ],
  },
];

export const RULE_TYPE_CONFIGS: readonly RuleTypeConfig[] = RULE_TYPE_GROUPS.flatMap((g) => g.configs);

/** Not in the picker: `dimension_affinity` is half of a Content interest pair, `has_not_tag` the Tag row's "has none of". */
const PAIRED_RULE_TYPES: readonly RuleTypeConfig[] = [
  {
    type: 'dimension_affinity',
    icon: 'category',
    labelKey: 'data-platform.dimensions',
    descKey: 'data-platform.rule-desc-action-dimension',
  },
  {
    type: 'has_not_tag',
    icon: 'label_off',
    labelKey: 'data-platform.rule-has-tag',
    descKey: 'data-platform.rule-desc-has-tag',
  },
];

export const RULE_TYPE_ICON_MAP: Readonly<Record<string, string>> = Object.fromEntries(
  [...RULE_TYPE_CONFIGS, ...PAIRED_RULE_TYPES].map((c) => [c.type, c.icon]),
);

export const RULE_TYPE_LABEL_MAP: Readonly<Record<string, string>> = Object.fromEntries(
  [...RULE_TYPE_CONFIGS, ...PAIRED_RULE_TYPES].map((c) => [c.type, c.labelKey]),
);

export interface OperatorOption {
  value: string;
  labelKey: string;
}

export const OPERATOR_OPTIONS: readonly OperatorOption[] = [
  { value: 'gte', labelKey: 'data-platform.op-gte' },
  { value: 'gt', labelKey: 'data-platform.op-gt' },
  { value: 'eq', labelKey: 'data-platform.op-eq' },
  { value: 'lt', labelKey: 'data-platform.op-lt' },
  { value: 'lte', labelKey: 'data-platform.op-lte' },
  { value: 'between', labelKey: 'data-platform.op-between' },
];

export const DAYS_AGO_OPERATOR_OPTIONS: readonly OperatorOption[] = [
  { value: 'lte', labelKey: 'data-platform.tf-time-window' },
  { value: 'between', labelKey: 'data-platform.tf-between-days-ago' },
];

/** What the stored goal and purchase filters can express. */
export const COUNT_OPERATOR_OPTIONS: readonly OperatorOption[] = [
  { value: 'gte', labelKey: 'data-platform.op-gte' },
  { value: 'gt', labelKey: 'data-platform.op-gt' },
  { value: 'eq', labelKey: 'data-platform.op-eq' },
  { value: 'none', labelKey: 'data-platform.op-none' },
];

/** What the stored revenue filter can express. */
export const REVENUE_OPERATOR_OPTIONS: readonly OperatorOption[] = [
  { value: 'gt', labelKey: 'data-platform.op-gt' },
  { value: 'lt', labelKey: 'data-platform.op-lt' },
  { value: 'eq', labelKey: 'data-platform.op-eq' },
  { value: 'between', labelKey: 'data-platform.op-between' },
];

export const TIMEFRAME_MODE_OPTIONS: readonly OperatorOption[] = [
  { value: '', labelKey: 'data-platform.tf-any-time' },
  { value: 'TimeWindow', labelKey: 'data-platform.tf-time-window' },
  { value: 'SinceDate', labelKey: 'data-platform.tf-since-date' },
  { value: 'BetweenDates', labelKey: 'data-platform.tf-between-dates' },
  { value: 'BetweenDaysAgo', labelKey: 'data-platform.tf-between-days-ago' },
];

/**
 * Derives the timeframeMode value from a rule's timeframe fields. Mirrors the
 * legacy builder's `deriveTimeframeMode` so existing rules round-trip without
 * losing their selected mode.
 */
export function deriveTimeframeMode(rule: {
  daysAgo?: number;
  daysAgoUpper?: number;
  startDate?: string;
  endDate?: string;
}): string {
  if (rule.daysAgoUpper != null) return 'BetweenDaysAgo';
  if (rule.startDate && rule.endDate) return 'BetweenDates';
  if (rule.startDate) return 'SinceDate';
  if (rule.daysAgo != null) return 'TimeWindow';
  return '';
}
